import React, { useState } from "react";
import { useL } from "@/lib/useL";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { PublicStats } from "@/components/PublicStats";
import { Sux1Logo } from "@/components/Sux1Logo";
import { Play, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";

const HERO_IMG = "/liberte-delacroix.jpg";

const HERO_FEATURES = [
  "3 IA de pointe : Claude Sonnet 4.5, GPT-5.2, Gemini 3 Flash",
  "12 formats : paragraphes, points clés, enseignant, révision, DYS, dissertation, résolution…",
  "Textes, PDF, DOCX, EPUB, images et URL — de l'article au livre entier",
  "Exemples gratuits · Dès 2 € les 20 résumés, sans abonnement",
  "Bilingue FR/EN · Mode hors-ligne · Historique persistant",
];

const HERO_FEATURES_EN = [
  "3 leading AIs: Claude Sonnet 4.5, GPT-5.2, Gemini 3 Flash",
  "12 formats: paragraphs, key points, teacher, revision, DYS, essay, solution…",
  "Text, PDF, DOCX, EPUB, images and URLs, from an article to a whole book",
  "Free examples · From €2 for 20 summaries, no subscription",
  "Bilingual FR/EN · Offline mode · Saved history",
];

export default function Login() {
  const L = useL();
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success(t("auth.welcome_back"));
      const dest = loc.state?.from?.pathname || "/";
      nav(dest, { replace: true });
    } catch (err) {
      if (err?.response?.status === 429) setLocked(err.response.data?.detail);
      else toast.error(err?.response?.status === 401 ? t("auth.invalid_credentials", "E-mail ou mot de passe incorrect.") : err?.response?.data?.detail || t("auth.login_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const [forgotBusy, setForgotBusy] = useState(false);
  const onForgot = async () => {
    if (!/\S+@\S+\.\S+/.test(email.trim())) {
      toast("Saisissez d'abord votre adresse email dans le champ ci-dessus, puis cliquez à nouveau.");
      return;
    }
    setForgotBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      toast.success(data.message, { duration: 9000 });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast(typeof d === "string" ? d : L("Envoi impossible pour le moment. Réessayez plus tard.", "Cannot send right now. Please try again later."));
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-12">
      <div className="lg:col-span-5 flex flex-col p-8 lg:p-16 border-r border-border">
        <div className="flex items-center justify-between mb-16">
          <Link to="/" className="flex items-center">
            <Sux1Logo size="lg" />
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </div>

        <div className="max-w-md w-full mx-auto flex-1 flex flex-col justify-center">
          <div className="sux-marker mb-3">{t("auth.section_auth")}</div>
          <h1 className="text-4xl sm:text-5xl font-black mb-4 leading-[0.95]">
            {t("auth.login_title")}
          </h1>
          <p className="text-muted-foreground mb-10 text-sm">
            {t("auth.login_subtitle")}
          </p>

          {locked && (
            <div role="alert" data-testid="login-locked-alert" className="mb-6 border border-[#FF2A00] bg-[#FF2A00]/5 p-4 text-sm">
              <p className="font-semibold">{locked}</p>
              <button type="button" onClick={onForgot} disabled={forgotBusy} data-testid="login-locked-forgot-btn"
                className="mt-2 font-mono uppercase tracking-[0.14em] text-[11px] text-[#002FA7] underline disabled:opacity-50">
                {L("Mot de passe oublié ?", "Forgot password?")}
              </button>
            </div>
          )}
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="sux-marker block mb-2">{t("auth.email")}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.email_placeholder")}
                data-testid={AUTH.loginEmail}
                className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
              />
            </div>
            <div>
              <label className="sux-marker block mb-2">{t("auth.password")}</label>
              <PasswordInput
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                testid={AUTH.loginPassword}
                className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
              />
              <button
                type="button"
                onClick={onForgot}
                disabled={forgotBusy}
                data-testid="login-forgot-password"
                className="mt-2 font-mono uppercase tracking-[0.14em] text-[11px] text-muted-foreground hover:text-[#002FA7] transition-colors disabled:opacity-50"
              >
                {L("Mot de passe oublié ?", "Forgot password?")}
              </button>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid={AUTH.loginSubmit}
              className="w-full bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-xs py-4 hover:bg-[#00227A] transition-colors disabled:opacity-50"
            >
              {submitting ? t("auth.signing_in") : t("auth.signin_btn")}
            </button>
          </form>

          <Link
            to="/signup"
            data-testid={AUTH.loginToSignup}
            className="mt-10 pt-6 border-t border-border flex items-center justify-between hover:opacity-75 transition-opacity"
          >
            <span className="sux-marker">{t("auth.no_account")}</span>
            <span className="font-mono uppercase tracking-[0.2em] text-[11px] underline underline-offset-4 decoration-[#002FA7] hover:text-[#002FA7]">
              {t("auth.create_one")}
            </span>
          </Link>

          <Link
            to="/demo"
            data-testid="login-see-demo"
            className="mt-6 inline-flex items-center justify-center gap-2 w-full border border-[#002FA7] text-[#002FA7] font-mono uppercase tracking-[0.2em] text-[11px] py-3 hover:bg-[#002FA7] hover:text-white transition-colors"
          >
            <Play className="w-3.5 h-3.5" fill="currentColor" /> Voir SUX1 en action
          </Link>

          <Link
            to="/showcase"
            data-testid="login-see-showcase"
            className="mt-3 inline-flex items-center justify-center gap-2 w-full font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <Sparkles className="w-3 h-3" /> {L("Voir 4 résumés déjà générés", "See 4 summaries already generated")}
          </Link>

          <Link
            to="/enseignants"
            data-testid="login-see-enseignants"
            className="mt-3 inline-flex items-center justify-center gap-2 w-full font-mono uppercase tracking-[0.2em] text-[10px] text-[#002FA7] hover:text-[#FF2A00] transition-colors"
          >
            {L("Livre : L'IA pour enseignants", "Book: AI for teachers (in French)")}
            <span className="px-1.5 py-0.5 bg-[#FF2A00] text-white text-[8px] leading-none tracking-normal">
              NEW
            </span>
          </Link>

          <PublicStats />
        </div>

        {/* Mobile-only feature rotator — the desktop hero image (Delacroix)
            is hidden on <lg screens, so we surface these 5 key selling
            points here so mobile visitors also get the value pitch. */}
        <div className="lg:hidden -mx-8 mt-10">
          <HeroFeatureRotator features={HERO_FEATURES} variant="mobile" />
        </div>

        <div className="mt-16 pt-6 border-t border-border flex flex-wrap items-center gap-x-6 gap-y-3">
          <span className="sux-marker">{t("auth.footer")}</span>
          <span className="ml-auto flex items-center gap-4">
            <Link
              to="/about"
              data-testid="login-about-link"
              className="font-mono uppercase tracking-[0.2em] text-[11px] text-muted-foreground hover:text-[#002FA7] transition-colors"
            >
              {t("nav.about")}
            </Link>
            <Link
              to="/privacy"
              data-testid="login-privacy-link"
              className="font-mono uppercase tracking-[0.2em] text-[11px] text-muted-foreground hover:text-[#002FA7] transition-colors"
            >
              {t("nav.privacy", { defaultValue: "Confidentialité" })}
            </Link>
            <a
              href="mailto:fiestamax@gmail.com?subject=%5BSUX1%5D%20Contact"
              data-testid="login-contact-link"
              className="font-mono uppercase tracking-[0.2em] text-[11px] text-muted-foreground hover:text-[#002FA7] transition-colors"
            >
              Contact
            </a>
          </span>
        </div>
      </div>

      <AuthHeroPanel />
    </div>
  );
}

// Shared right-hand visual for /login and /signup (Delacroix + feature carousel).
export function AuthHeroPanel() {
  const L = useL();
  return (
    <div className="hidden lg:block lg:col-span-7 relative bg-[#002FA7]">
      <img src={HERO_IMG} alt={L("La Liberté guidant le peuple — Eugène Delacroix, 1830", "Liberty Leading the People — Eugène Delacroix, 1830")} className="w-full h-full object-cover opacity-90" />
      {/* Stronger bottom-to-top gradient so the white text stays readable
          regardless of where the browser fold sits (900px, 1080px, etc.) */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#002FA7]/90 via-[#002FA7]/55 to-[#002FA7]/10" />
      {/* Position the tagline block around 55% of the hero height so the
          rotator sits comfortably above any cookie banner / fold. */}
      <div className="absolute left-12 right-12 text-white" style={{ top: "42%" }}>
        <div className="sux-marker-light mb-4">{L("Édition // 2026", "Edition // 2026")}</div>
        <p className="text-2xl lg:text-3xl font-light leading-tight max-w-xl mb-3" style={{ fontFamily: "Outfit" }}>
          {L("« La précision est une discipline. Le résumé est sa forme la plus pure. »", "« Precision is a discipline. The summary is its purest form. »")}
        </p>
        <div className="font-mono uppercase tracking-[0.24em] text-[10px] text-white/70 mb-8">
          — Jérôme Rognin-Picard, {L("fondateur de SUX1", "founder of SUX1")}
        </div>
        <HeroFeatureRotator features={HERO_FEATURES} />
      </div>
    </div>
  );
}

/**
 * Discreet feature carousel: displays one line at a time, rotating every
 * 4.5s with an editorial slide+fade so the transition is clearly seen.
 * A thin red progress bar fills across the 4.5s window as a visual cue.
 * Pauses on hover.
 */
function HeroFeatureRotator({ features: frFeatures, variant = "desktop" }) {
  const L = useL();
  const features = L(frFeatures, HERO_FEATURES_EN);
  const [idx, setIdx] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  React.useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % features.length), 4500);
    return () => clearInterval(t);
  }, [paused, features.length]);

  const isMobile = variant === "mobile";
  const containerBase = isMobile
    ? "bg-primary text-white px-5 py-6 border-t border-b border-border"
    : "border-t border-white/25 pt-5 text-left";

  return (
    <div
      className={containerBase}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid={`hero-features-rotator${isMobile ? "-mobile" : ""}`}
    >
      <div className={`font-mono uppercase tracking-[0.22em] text-[10px] mb-3 ${isMobile ? "text-white/60" : "text-white/70"}`}>
        {L("Fonctionnalités", "Features")}
      </div>
      <p
        key={idx}
        className={`animate-fadein font-medium leading-snug ${isMobile ? "text-white text-[13px]" : "text-white text-base md:text-lg"}`}
        style={{ minHeight: "1.6em" }}
        data-testid={`hero-feature-${idx}${isMobile ? "-mobile" : ""}`}
      >
        {features[idx]}
      </p>
      {/* Red progress bar — restarts on every rotation via the `key` prop */}
      <div className="mt-4 h-[2px] w-full bg-white/15 overflow-hidden">
        <div
          key={`p-${idx}-${paused}`}
          className={paused ? "h-full bg-[#FF2A00] w-full" : "h-full bg-[#FF2A00] hero-progress"}
        />
      </div>
      <div className="flex gap-1.5 mt-3">
        {features.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            aria-label={`${L("Fonctionnalité", "Feature")} ${i + 1}`}
            data-testid={`hero-dot-${i}${isMobile ? "-mobile" : ""}`}
            className={`h-[3px] transition-all ${i === idx ? "w-8 bg-white" : "w-3 bg-white/30 hover:bg-white/60"}`}
          />
        ))}
      </div>
    </div>
  );
}
